import { createContext, useContext, useState, useEffect, useRef } from 'react';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const isLogin = useRef(null);
  const [user, setUser] = useState(null);
  const token = useRef(localStorage.getItem('token') || null);
  const host = process.env.REACT_APP_RESTAPI_URL;
  const isLoading = useRef(false);

  // 네트워크 레벨 실패("Failed to fetch")는 끊긴 keep-alive 연결 재사용 등
  // 일시적 원인이 많아 짧게 자동 재시도한다. (HTTP 에러 응답은 재시도하지 않음)
  const fetchWithRetry = async (url, options, retries = 2) => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await fetch(url, options);
      } catch (err) {
        if (attempt >= retries) throw err;
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
  };

  // fetch 자체가 거부되면(TypeError: Failed to fetch) 원문 대신 안내 메시지로 대체
  const friendlyError = (error) =>
    error instanceof TypeError || /Failed to fetch/i.test(error?.message || '')
      ? '네트워크가 불안정합니다. 잠시 후 다시 시도해 주세요.'
      : error.message;

  // isLogin 상태 변화 감지하여 리다이렉트
  useEffect(() => {
    if (isLogin.current === false) {
      // 현재 페이지가 /가 아닌 경우에만 리다이렉트
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
  }, [user]); // user 상태가 변할 때마다 체크

  useEffect(() => {
    getUser(token.current);
    // eslint-disable-next-line
  }, [token.current]);

  const getUser = (token) => {
    if (!token) {
      isLogin.current = false;
      setUser(null);
      return null;
    }

    isLoading.current = true;
    const url = `${host}/oauth/user`;
    console.log(`Fetching user data from URL: ${url}`);

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    console.log(`Headers:`, headers);

    fetchWithRetry(url, {
        method: 'GET',
        headers,
      })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            isLogin.current = false;
            throw new Error(errorData.message || '알 수 없는 오류가 발생했습니다.');
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log(`현재 시간: ${new Date().toISOString()}`);
        console.log('사용자 정보:', data);
        isLogin.current = true;
        setUser(data);
      })
      .catch((error) => {
        console.error('Error fetching user data:', error.message);
        alert(friendlyError(error));
        localStorage.removeItem('token');
        isLogin.current = false;
        setUser(null);
        // user가 이미 null인 경우(최초 접속 등) setUser(null)은 상태 변화가 없어
        // [user]를 감시하는 useEffect가 재실행되지 않으므로, 여기서 직접 이동시킨다.
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      })
      .finally(() => {
        isLoading.current = false;
      });
  };

  const login = (id, passwd) => {
    if (isLoading.current) return;
    isLoading.current = true;
    const url = `${host}/oauth/login`;
    console.log(`Logging in with URL: ${url} id: ${id} passwd: ${passwd}`);

    fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, passwd }),
      })
      .then((response) => {
        console.log('response : ', response);
        if (!response.ok) {
          return response.json().then((errorData) => {
            throw new Error(errorData.message || '알 수 없는 오류가 발생했습니다.');
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
        token.current = data;
        localStorage.setItem('token', data);
        isLogin.current = true;
        getUser(data);
      })
      .catch((error) => {
        console.error('Login error:', error);
        alert(friendlyError(error));
        isLogin.current = false;
      })
      .finally(() => {
        isLoading.current = false;
      });
  }

  const logout = () => {
    if (isLoading.current) return;
    localStorage.removeItem('token');
    isLogin.current = false; // null 대신 false로 변경
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, setUser, login, logout, isLoading, isLogin }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
export { UserContext };